
import React, { useState, useEffect, useMemo } from 'react';
import { Individual, BookingStatus, EventType } from '../types';
import { COUNTRIES_CURRENCIES, MOCK_BUSINESSES, TIME_SLOTS } from '../constants';
import { X, Calendar, MapPin, Users, ArrowRight, ArrowLeft, Check, Sparkles, User, Globe, Tag, Clock, Phone, Mail, Hash } from 'lucide-react';

interface CreateEventWizardProps {
  onSave: (individual: Individual) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialEmail?: string;
  initialPhone?: string;
  existingEvents?: Individual[];
}

const steps = [
  { id: 1, title: 'The Vision', icon: Calendar, description: 'Event type & Guest count' },
  { id: 2, title: 'The Logistics', icon: MapPin, description: 'Location & Time' },
  { id: 3, title: 'The Details', icon: User, description: 'Final touches' },
];

const CreateEventWizard: React.FC<CreateEventWizardProps> = ({ onSave, onCancel, isOpen, initialEmail, initialPhone, existingEvents = [] }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Individual>>({
    eventType: EventType.Wedding,
    eventName: '',
    name: '',
    partnerName: '',
    email: initialEmail?.toLowerCase() || '',
    phone: initialPhone || '',
    weddingDate: '',
    eventTime: '',
    dueDate: '',
    venue: '',
    district: '',
    city: '',
    country: '',
    currency: 'USD',
    guestCount: 0,
    budget: 0,
    status: BookingStatus.Inquiry,
    notes: '',
    preferences: ''
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFormData(prev => ({
        ...prev,
        eventType: EventType.Wedding,
        email: initialEmail?.toLowerCase() || prev.email || '',
        phone: initialPhone || prev.phone || '',
        country: 'Saudi Arabia',
        currency: 'SAR'
      }));
    }
  }, [isOpen, initialEmail, initialPhone]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'country') {
      const selected = COUNTRIES_CURRENCIES.find(c => c.country === value);
      setFormData(prev => ({
        ...prev,
        country: value,
        currency: selected ? selected.currency : 'USD'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'guestCount' || name === 'budget' ? Number(value) : value
      }));
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.eventType) return alert("Select Event Type");
      if (!formData.weddingDate) return alert("Select a date");
      if (!formData.guestCount || formData.guestCount <= 0) return alert("Please specify the number of guests.");
    }
    if (currentStep === 2) {
      if (!formData.country || !formData.city) return alert("Location details required");
    }
    if (currentStep === 3) {
      if (!formData.name) return alert("Client name required");
      handleSubmit();
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = () => {
    // Ensure email is lowercased one last time before saving
    onSave({ 
      ...formData, 
      id: crypto.randomUUID(),
      email: formData.email?.toLowerCase() || initialEmail?.toLowerCase() || "" 
    } as Individual);
  };

  const standardTypes = Object.values(EventType).filter(t => t !== EventType.Other);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-4xl flex flex-col md:flex-row overflow-hidden animate-fadeIn h-[700px] border border-white/20">
        
        {/* Step Indicator Sidebar */}
        <div className="bg-wedding-900 p-10 md:w-1/3 flex flex-col justify-between text-white relative">
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
             <div className="absolute top-1/4 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-10">
               <Sparkles className="w-8 h-8 text-wedding-300" />
               <h2 className="text-2xl font-serif font-black tracking-tight">Plan Vision</h2>
            </div>
            
            <div className="space-y-12">
              {steps.map((step) => {
                const isActive = step.id === currentStep;
                const isCompleted = step.id < currentStep;
                return (
                  <div key={step.id} className="flex items-start space-x-4">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all shadow-lg ${isActive ? 'bg-wedding-500 scale-110' : isCompleted ? 'bg-emerald-500' : 'bg-wedding-800'}`}>
                      {isCompleted ? <Check className="w-5 h-5" /> : <span className="font-black">{step.id}</span>}
                    </div>
                    <div>
                      <p className={`font-black text-xs uppercase tracking-[0.2em] ${isActive ? 'text-wedding-200' : 'text-wedding-600'}`}>{step.title}</p>
                      <p className="text-xs text-white/50 font-medium">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="text-[10px] font-black uppercase tracking-widest text-wedding-700 mt-10">
            Secure Planning Node V3.4
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col bg-white">
          <div className="flex justify-end p-6">
            <button onClick={onCancel} className="text-gray-300 hover:text-gray-900 transition-colors">
              <X className="w-8 h-8" />
            </button>
          </div>

          <div className="flex-1 px-12 pb-12 overflow-y-auto custom-scrollbar">
            {currentStep === 1 && (
              <div className="space-y-8 animate-fadeIn">
                <div className="text-center mb-10">
                   <h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">The Vision</h3>
                   <p className="text-gray-400 font-medium uppercase tracking-widest text-[10px]">What are we orchestrating?</p>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Event Category</label>
                    <div className="relative group">
                       <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500" />
                       <select name="eventType" value={formData.eventType} onChange={handleChange} className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 outline-none font-bold text-sm">
                         {standardTypes.map(t => <option key={t} value={t}>{t}</option>)}
                       </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Event Title</label>
                    <input type="text" name="eventName" value={formData.eventName} onChange={handleChange} placeholder="e.g. Royal Wedding Reception" className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 outline-none text-sm font-medium" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-1">Proposed Date</label>
                      <input type="date" name="weddingDate" value={formData.weddingDate} onChange={handleChange} className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 outline-none font-bold" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-wedding-600 uppercase tracking-widest mb-3 ml-1">Number of People *</label>
                      <div className="relative group">
                        <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-wedding-500" />
                        <input type="number" name="guestCount" value={formData.guestCount} onChange={handleChange} min="1" placeholder="e.g. 150" className="w-full pl-12 pr-4 py-4 bg-wedding-50 border border-wedding-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/20 outline-none font-black text-wedding-900" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-8 animate-fadeIn">
                 <div className="text-center mb-10"><h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">The Logistics</h3></div>
                 <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Region</label>
                    <select name="country" value={formData.country} onChange={handleChange} className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold">
                       {COUNTRIES_CURRENCIES.map(c => <option key={c.country} value={c.country}>{c.flag} {c.country}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">City</label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Riyadh" className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none" />
                    </div>
                    <div>
                       <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Time Preference</label>
                       <select name="eventTime" value={formData.eventTime} onChange={handleChange} className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold">
                          {TIME_SLOTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                       </select>
                    </div>
                  </div>
                 </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-8 animate-fadeIn">
                 <div className="text-center mb-10"><h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">The Details</h3></div>
                 <div className="space-y-6">
                   <div className="relative group">
                     <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                     <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Client Full Name *" className="w-full pl-12 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold" />
                   </div>
                   <div className="relative group">
                     <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                     <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Client Email" className="w-full pl-12 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold" />
                   </div>
                   <div className="relative group">
                     <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                     <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Mobile Number" className="w-full pl-12 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold tracking-widest" />
                   </div>
                 </div>
              </div>
            )}
          </div>

          {/* Footer Controls */}
          <div className="p-10 border-t border-gray-50 flex justify-between bg-gray-50/50">
            <button onClick={prevStep} disabled={currentStep === 1} className="px-8 py-4 rounded-2xl font-black uppercase tracking-widest flex items-center text-gray-400 disabled:opacity-20 transition-all hover:text-gray-900">
               <ArrowLeft className="w-5 h-5 mr-2" />Back
            </button>
            <button onClick={nextStep} className="px-12 py-4 bg-wedding-600 text-white rounded-2xl font-black uppercase tracking-widest flex items-center shadow-xl shadow-wedding-200 hover:bg-wedding-700 transition-all transform active:scale-95">
               {currentStep === 3 ? <><Check className="w-5 h-5 mr-2" /> Orchestrate</> : <>Next <ArrowRight className="w-5 h-5 ml-2" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEventWizard;
